import { useState, useEffect } from "react";

import {
  School,
  GraduationCap,
  Users2,
  BookOpen,
  PlayCircle,
  ClipboardList,
  LineChart,
  BarChart3,
  MapPin,
  Building2,
  Star,
  Clock,
  CheckCircle2,
  UserX,
  Activity
} from "lucide-react";

import { BuscaNome, SemResultadoBusca } from "../../components/BuscaNome";
import { AreaChartCard, NotaBarraLista } from "../../components/charts/Charts";
import { EvolucaoTemporal } from "../../components/charts/EvolucaoTemporal";
import { FaixaKpiCompacta } from "../../components/charts/FaixaKpiCompacta";
import { EngajamentoCell } from "../../components/layout/EngajamentoCell";
import { KpiCard } from "../../components/layout/KpiCard";
import { NotaCell } from "../../components/layout/NotaCell";
import { RankingTabela } from "../../components/layout/RankingTabela";
import {
  Card,
  SectionHead,
  Skeleton,
  EmptyState,
  EstadoVazio,
  Chip
} from "../../components/ui/primitives";
import { api } from "../../lib/api";
import { filtrarPorNome } from "../../lib/busca";
import { brPt, gradeColor, fmtTempo } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { useDebounce } from "../../lib/useDebounce";
import { scrollPainelTopo } from "../../lib/utils";
import { AdminPainelView } from "../admin/AdminPainel";
import { TurmaPainelView } from "../professor/TurmaPainel";

export function GestorPainelView({
  grupoId,
  grupoNome,
  periodo,
  onAno,
  onBreadcrumb,
  regiaoFixa = null,
  educandus = false
}) {
  // regiaoFixa (D3.1): quando presente, o painel abre ANCORADO nessa região e
  // não permite subir ao nível da rede. É assim que o administrador de regional
  // (papel 3) reusa este painel sem ver as regiões irmãs nem os KPIs da rede.
  const [regiaoSel, setRegiaoSel] = useState(regiaoFixa || null);
  const [escolaSel, setEscolaSel] = useState(null);
  const [turmaSel, setTurmaSel] = useState(null);
  const [metaEscola, setMetaEscola] = useState(null);

  // ─── Nível das REDES, um degrau acima (Administrador Educandus) ────────────
  // Com `educandus`, o ranking do rodapé lista REDES antes de listar regiões —
  // pedido do Kilson em 04/08/2026, e não é preferência de layout: o ranking de
  // regiões nesse nível trazia as 74 regiões dos 7 clientes misturadas, e os
  // nomes se repetem entre eles ("PE" existe em 5 grupos, "BA" em 4). Eram cinco
  // linhas chamadas "PE" sem dizer de qual cliente era cada uma.
  //
  // Daí para baixo o fluxo é o padrão (rede → região → escola → turma), com o
  // mesmo código: `grupoEfetivo` alimenta as chamadas.
  //
  // A rede vinda do modal do AVA é PONTO DE PARTIDA, não prisão: entra como
  // seleção inicial e o breadcrumb continua permitindo subir ao ranking de
  // redes. Foi o defeito da primeira versão deste degrau — quem tivesse
  // escolhido uma rede no modal (e o redux-persist guarda a escolha entre
  // sessões) abria o painel preso nela, sem rede nenhuma para clicar. Relato do
  // Kilson: "a tela do ranking não me deixa clicar na rede que quero explorar".
  const [redeSel, setRedeSel] = useState(
    educandus && grupoId ? { id: grupoId, rede: grupoNome } : null
  );

  // Para o Educandus o nível vem SÓ da navegação; para os outros perfis, da
  // prop — eles não têm degrau de redes a que subir.
  const grupoEfetivo = educandus ? redeSel?.id ?? null : grupoId;
  const nivelEducandus = educandus;
  // Está no degrau das redes: nenhuma escolhida, nem pela prop nem no ranking.
  const semRede = !grupoEfetivo;

  // Trocar de rede no modal do AVA com o painel aberto recomeça a navegação:
  // os ids de região/escola/turma da rede anterior não valem para a nova.
  // Só para o Educandus — para os demais, mexer aqui apagaria a `regiaoFixa`
  // do administrador de regional na primeira renderização.
  useEffect(() => {
    if (!educandus) return;
    setRedeSel(grupoId ? { id: grupoId, rede: grupoNome } : null);
    setRegiaoSel(null);
    setEscolaSel(null);
    setTurmaSel(null);
  }, [educandus, grupoId, grupoNome]);

  // KPIs do nível atual (rede quando nenhuma região está selecionada; região
  // quando está), recortados pelo filtro global de período. Não busca dentro de
  // uma escola — esse recorte tem painel próprio (AdminPainelView).
  const kpis = useAsync(
    () =>
      escolaSel
        ? Promise.resolve(null)
        : api.kpisGlobal(
            regiaoSel
              ? {
                  region_id: regiaoSel.id,
                  inicio: periodo.inicio,
                  fim: periodo.fim
                }
              : {
                  grupo_id: grupoEfetivo,
                  inicio: periodo.inicio,
                  fim: periodo.fim
                }
          ),
    [grupoEfetivo, regiaoSel, escolaSel, periodo.inicio, periodo.fim]
  );
  const execMensal = useAsync(
    () => api.execucoesMensal(grupoEfetivo),
    [grupoEfetivo]
  );

  // Ranking de redes: só no nível da Educandus e antes de escolher uma.
  //
  // No modo mock isto nunca dispara, e de propósito: useContextoIE força
  // `grupoId` para o primeiro grupo das fixtures, então `nivelEducandus` é
  // falso. É o que dispensa uma fixture nova para /grupos/ranking — o mock
  // reprova rota não mapeada em alto e bom som, e reprovaria esta.
  const redes = useAsync(
    () =>
      nivelEducandus && !redeSel ? api.gruposRanking() : Promise.resolve(null),
    [nivelEducandus, redeSel]
  );

  // Ranking de regiões: PRECISA de uma rede. Sem ela viriam as regiões de todos
  // os clientes na mesma tabela, com nomes repetidos entre eles — é o defeito
  // que o nível de redes resolve, e buscar aqui só gastaria a chamada.
  const regioes = useAsync(
    () =>
      grupoEfetivo ? api.regionalRanking(grupoEfetivo) : Promise.resolve(null),
    [grupoEfetivo]
  );

  // Faixa (snapshot, sem período — igual ao Admin), evolução e disciplina
  // (recortadas pelo período) do nível atual.
  const faixas = useAsync(
    () =>
      escolaSel
        ? Promise.resolve([])
        : api.faixas(
            regiaoSel ? { region_id: regiaoSel.id } : { grupo_id: grupoEfetivo }
          ),
    [grupoEfetivo, regiaoSel, escolaSel]
  );
  const evolucao = useAsync(
    () =>
      escolaSel
        ? Promise.resolve([])
        : api.evolucao(
            regiaoSel
              ? {
                  region_id: regiaoSel.id,
                  inicio: periodo.inicio,
                  fim: periodo.fim
                }
              : {
                  grupo_id: grupoEfetivo,
                  inicio: periodo.inicio,
                  fim: periodo.fim
                }
          ),
    [grupoEfetivo, regiaoSel, escolaSel, periodo.inicio, periodo.fim]
  );
  const disciplinas = useAsync(
    () =>
      escolaSel
        ? Promise.resolve([])
        : api.disciplinasDesempenho(
            undefined,
            undefined,
            regiaoSel ? undefined : grupoEfetivo,
            regiaoSel?.id,
            periodo.inicio,
            periodo.fim
          ),
    [grupoEfetivo, regiaoSel, escolaSel, periodo.inicio, periodo.fim]
  );

  // Ano que ancora os presets do filtro no topbar: o da escola quando se entra
  // numa unidade (resolvido via onMeta do AdminPainelView), nada na rede/região.
  useEffect(() => {
    onAno?.(escolaSel ? metaEscola?.ano ?? undefined : undefined);
  }, [escolaSel, metaEscola, onAno]);

  // ─── Busca por nome nas três listas ───────────────────────────────────────
  // Redes e regiões vêm completas, então filtram no cliente. Escolas NÃO: a
  // chamada traz 100 de até 990, e filtrar aqui esconderia da busca tudo o que
  // está fora da página — por isso o termo de escola vai ao servidor, no
  // parâmetro `nome` que a rota /escolas já aceita (ILIKE).
  const [buscaRede, setBuscaRede] = useState("");
  const [buscaRegiao, setBuscaRegiao] = useState("");
  const [buscaEscola, setBuscaEscola] = useState("");

  const buscaEscolaServidor = useDebounce(buscaEscola, 350);

  const redesFiltradas = filtrarPorNome(redes.data, buscaRede, ["rede"]);
  const regioesFiltradas = filtrarPorNome(regioes.data, buscaRegiao, [
    "regional"
  ]);

  // Também precisa de uma rede: no degrau das redes isto traria as escolas de
  // todos os clientes, e a lista só é exibida dentro de uma região.
  const escolas = useAsync(
    () =>
      grupoEfetivo
        ? api.escolas({
            per_page: 100,
            grupo_id: grupoEfetivo,
            region_id: regiaoSel?.id,
            nome: buscaEscolaServidor || undefined
          })
        : Promise.resolve(null),
    [grupoEfetivo, regiaoSel, buscaEscolaServidor]
  );

  // Toda seleção que aprofunda a navegação (região → escola → turma → aluno)
  // volta o scroll ao topo e re-anima a página — mesmo padrão do Admin, onde
  // o próprio drawer já nasce rolado no topo. A árvore de "rede" (KPIs,
  // execuções, disciplina, ranking) leva um key baseado na região (ver
  // return abaixo), então entrar/sair de uma região remonta a página
  // inteira do zero, tocando a mesma animação de montagem em todos os cards.
  // Escolher a rede no ranking é o degrau novo, e daqui para baixo tudo é o
  // fluxo padrão — não há caminho separado para o Administrador Educandus.
  function selecionarRede(r) {
    setRedeSel(r);
    setRegiaoSel(null);
    setEscolaSel(null);
    setTurmaSel(null);
    scrollPainelTopo();
  }
  function voltarRedes() {
    setRedeSel(null);
    setRegiaoSel(null);
    setEscolaSel(null);
    setTurmaSel(null);
    scrollPainelTopo();
  }
  function selecionarRegiao(r) {
    setRegiaoSel(r);
    scrollPainelTopo();
  }
  function selecionarEscola(e) {
    setEscolaSel(e);
    setMetaEscola(null);
    scrollPainelTopo();
  }
  function selecionarTurma(t) {
    setTurmaSel(t);
    scrollPainelTopo();
  }
  // "Voltar" pelo breadcrumb sempre volta ao topo da página que reaparece —
  // mesma regra de cima, nos dois sentidos (ida e volta).
  function voltarRegioes() {
    // com região fixa, "voltar" para a rede é proibido — volta ao nível da
    // própria região em vez de subir um degrau.
    setRegiaoSel(regiaoFixa || null);
    setEscolaSel(null);
    setTurmaSel(null);
    scrollPainelTopo();
  }
  function voltarEscolas() {
    setEscolaSel(null);
    setTurmaSel(null);
    scrollPainelTopo();
  }
  function voltarEscola() {
    setTurmaSel(null);
    scrollPainelTopo();
  }

  const crumbs = regiaoFixa ? [] : [];

  // Degrau da Educandus, acima da rede. Clicando nele, volta-se ao ranking de
  // redes.
  //
  // O rótulo é FIXO, e não o `grupoNome`: com uma rede escolhida no modal o
  // contexto passa a chamar-se "FORMANDO CIDADÃOS", e usá-lo aqui punha o mesmo
  // nome nos dois degraus da trilha ("FORMANDO CIDADÃOS › FORMANDO CIDADÃOS").
  // Este degrau é sempre o conjunto de todas as redes.
  if (!regiaoFixa && nivelEducandus) {
    crumbs.push({
      label: "Todas as redes",
      nivel: "educandus",
      onClick: redeSel ? voltarRedes : undefined
    });
  }

  // A rede: o nome vem da prop quando o perfil já está preso a uma (gestor de
  // rede), ou da rede selecionada (Educandus — pelo modal ou pelo ranking).
  if (!regiaoFixa && (!nivelEducandus || redeSel)) {
    crumbs.push({
      label: nivelEducandus ? redeSel.rede : grupoNome,
      nivel: "rede",
      onClick: regiaoSel ? voltarRegioes : undefined
    });
  }

  if (regiaoSel)
    crumbs.push({
      label: regiaoSel.regional,
      nivel: "regiao",
      onClick: escolaSel ? voltarEscolas : undefined
    });
  if (escolaSel)
    crumbs.push({
      label: escolaSel.escola,
      nivel: "escola",
      onClick: turmaSel ? voltarEscola : undefined
    });
  if (turmaSel) crumbs.push({ label: turmaSel.turma, nivel: "turma" });

  // Reporta a trilha pro cabeçalho principal (AppShell) — mesmo padrão do
  // onAno acima. Roda num efeito (não no corpo do render) pra não disparar
  // setState no componente pai durante o render deste.
  useEffect(() => {
    onBreadcrumb?.(crumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoNome, redeSel, regiaoSel, escolaSel, turmaSel, onBreadcrumb]);

  // ── Escola selecionada: substitui o painel inteiro (sem KPIs da rede) ──────
  // Antes dos guards de loading da rede/região: o mesmo filtro de período vale
  // aqui dentro (via AdminPainelView/TurmaPainelView), e trocar o período não
  // deve piscar o skeleton da rede por baixo.
  if (escolaSel) {
    return turmaSel ? (
      // Mesma tela do perfil do professor (KPIs, evolução, progresso,
      // execução, engajamento, tabela completa de alunos) — inclui seu
      // próprio AlunoDrawer internamente, sem precisar levantar estado
      // de aluno aqui.
      <TurmaPainelView
        turmaId={turmaSel.id}
        ano={metaEscola?.ano ?? undefined}
        periodo={periodo}
      />
    ) : (
      <AdminPainelView
        escolaId={escolaSel.id}
        periodo={periodo}
        onMeta={setMetaEscola}
        onSelectTurma={selecionarTurma}
      />
    );
  }

  // O erro vem ANTES do skeleton, e a ordem é o defeito que faltava: em erro o
  // useAsync devolve `{data: null, loading: false, error}`, então a guarda de
  // baixo (que também testa `!kpis.data`) capturava o caso e devolvia skeleton —
  // deixando a linha do EmptyState inalcançável. Todo erro deste painel virava
  // carregamento eterno, sem uma palavra na tela: foi assim que o 403 do
  // drill-down para região (problema 3 dos ajustes) apareceu como painel em
  // branco em vez de "sem permissão".
  if (kpis.error)
    return (
      <EmptyState
        title={`Não foi possível carregar ${regiaoSel ? "a região" : "a rede"}`}
        hint={kpis.error}
      />
    );

  // `!kpis.data` (além de `kpis.loading`): ao voltar de uma escola pro nível de
  // região/rede, o useAsync ainda carrega o `data: null` da sentinela usada
  // dentro da escola (fn resolvida como Promise.resolve(null), pra não buscar
  // à toa) — o refetch real só dispara no useEffect, um ciclo de render DEPOIS
  // de escolaSel virar null. Sem essa checagem extra, esse único render lia
  // `kpis.data!` como null e quebrava a tela (TypeError ao acessar k.escolas).
  if (kpis.loading || !kpis.data) return <PainelSkeleton />;
  const k = kpis.data;

  const colunasRegioes = [
    {
      key: "regional",
      label: "Região",
      render: r => (
        <span className="font-medium text-[var(--color-ink)]">
          {r.regional}
        </span>
      )
    },
    {
      key: "nota_media",
      label: "Nota média",
      num: true,
      valor: r => r.nota_media ?? -1,
      render: r =>
        r.nota_media == null ? (
          <span className="text-[var(--color-ink-faint)]">–</span>
        ) : (
          <NotaCell
            dados={{
              nota: r.nota_media,
              nota_rede: r.nota_rede,
              nota_avaliacao: r.nota_avaliacao,
              nota_regular: r.nota_regular,
              nota_tipo: r.nota_tipo
            }}
          />
        )
    },
    {
      key: "engajamento_medio",
      label: "Engajamento",
      num: true,
      valor: r => r.engajamento_medio ?? -1,
      render: r =>
        r.engajamento_medio == null ? (
          <span className="tnum text-[var(--color-ink-faint)]">–</span>
        ) : (
          <EngajamentoCell
            dados={{
              engajamento: r.engajamento_medio,
              eng_progresso: r.eng_progresso,
              eng_qualidade: r.eng_qualidade,
              eng_pontualidade: r.eng_pontualidade,
              eng_exploracao: r.eng_exploracao
            }}
          />
        )
    },
    {
      key: "taxa_conclusao",
      label: "Conclusão",
      num: true,
      valor: r => r.taxa_conclusao ?? 0,
      render: r => (
        <span className="tnum">
          {r.taxa_conclusao == null ? "–" : `${r.taxa_conclusao}%`}
        </span>
      )
    },
    {
      key: "tempo_total_seg",
      label: "Tempo",
      num: true,
      valor: r => r.tempo_total_seg,
      render: r => (
        <span className="tnum text-[var(--color-ink-soft)]">
          {fmtTempo(r.tempo_total_seg)}
        </span>
      )
    },
    {
      key: "execucoes",
      label: "Execuções",
      num: true,
      valor: r => r.execucoes,
      render: r => (
        <span className="tnum">{r.execucoes.toLocaleString("pt-BR")}</span>
      )
    },
    {
      key: "escolas",
      label: "Escolas",
      num: true,
      valor: r => r.escolas,
      render: r => <span className="tnum">{r.escolas}</span>
    },
    {
      key: "turmas",
      label: "Turmas",
      num: true,
      valor: r => r.turmas,
      render: r => <span className="tnum">{r.turmas}</span>
    }
  ];

  // O ranking de redes usa as MESMAS métricas do de regiões — só a primeira
  // coluna muda, porque a linha é uma rede. Herdar a lista em vez de copiá-la é
  // o que impede os dois níveis de exibirem colunas diferentes com o tempo; o
  // backend garante o outro lado, servindo o mesmo contrato nas duas rotas.
  const colunasRedes = [
    {
      key: "rede",
      label: "Rede",
      render: r => (
        <span className="font-medium text-[var(--color-ink)]">{r.rede}</span>
      )
    },
    ...colunasRegioes.slice(1)
  ];

  const colunasEscolas = [
    {
      key: "escola",
      label: "Escola",
      render: r => (
        <span className="font-medium text-[var(--color-ink)]">{r.escola}</span>
      )
    },
    {
      key: "regional",
      label: "Regional",
      render: r => (
        <span className="text-[var(--color-ink-soft)]">{r.regional}</span>
      )
    },
    {
      key: "nota_media",
      label: "Nota média",
      num: true,
      valor: r => r.nota_media ?? -1,
      render: r =>
        r.nota_media == null ? (
          <span className="text-[var(--color-ink-faint)]">–</span>
        ) : (
          <NotaCell
            dados={{
              nota: r.nota_media,
              nota_rede: r.nota_rede,
              nota_avaliacao: r.nota_avaliacao,
              nota_regular: r.nota_regular,
              nota_tipo: r.nota_tipo
            }}
          />
        )
    },
    {
      key: "engajamento_medio",
      label: "Engajamento",
      num: true,
      valor: r => r.engajamento_medio ?? -1,
      render: r =>
        r.engajamento_medio == null ? (
          <span className="tnum text-[var(--color-ink-faint)]">–</span>
        ) : (
          <EngajamentoCell
            dados={{
              engajamento: r.engajamento_medio,
              eng_progresso: r.eng_progresso,
              eng_qualidade: r.eng_qualidade,
              eng_pontualidade: r.eng_pontualidade,
              eng_exploracao: r.eng_exploracao
            }}
          />
        )
    },
    {
      key: "taxa_conclusao",
      label: "Conclusão",
      num: true,
      valor: r => r.taxa_conclusao ?? 0,
      render: r => (
        <span className="tnum">
          {r.taxa_conclusao == null ? "–" : `${r.taxa_conclusao}%`}
        </span>
      )
    },
    {
      key: "execucoes",
      label: "Execuções",
      num: true,
      valor: r => r.execucoes,
      render: r => (
        <span className="tnum">{r.execucoes.toLocaleString("pt-BR")}</span>
      )
    },
    {
      key: "turmas",
      label: "Turmas",
      num: true,
      valor: r => r.turmas,
      render: r => <span className="tnum">{r.turmas}</span>
    }
  ];

  // Métricas do nível atual — rede e região vêm do MESMO endpoint (kpis_global,
  // escopo grupo_id/region_id), já recortadas pelo período. Cabeçalho de KPIs é
  // o mesmo JSX nos dois escopos (ORDEM: 1ª parte = desempenho/avaliação, 2ª =
  // estrutura/volume; só muda o 1º card de estrutura, ver `regiaoSel` no JSX).
  const m = {
    nota: k.nota_media,
    conclusao: k.taxa_conclusao,
    engajamento: k.engajamento_medio,
    inativos: k.inativos,
    tempo: k.tempo_total_seg,
    escolas: k.escolas,
    turmas: k.turmas,
    alunos: k.alunos,
    professores: k.professores,
    execucoes: k.execucoes,
    tarefas: k.tarefas_atribuidas
  };
  const escopoLabel = regiaoSel ? "da região" : "da rede";

  return (
    // key força remonte da página inteira ao entrar/sair de uma região —
    // sem isso, só o conteúdo interno do card de ranking trocaria (os demais
    // cards já tinham "revelado" desde a montagem inicial e não animam de
    // novo) e a página não voltaria pro topo de fato (o card que mudou fica
    // no meio da árvore, não em uma seção isolada). Remontando tudo, a volta
    // ao topo da página e a animação de entrada valem pra cada card de novo,
    // igual à primeira montagem.
    <div
      key={regiaoSel ? `regiao-${regiaoSel.id}` : "rede"}
      className="space-y-7"
    >
      <section id="visao-geral" className="scroll-mt-4">
        <SectionHead
          title={
            regiaoSel
              ? `Visão geral — ${regiaoSel.regional}`
              : "Visão geral da rede"
          }
        />
        <div className="space-y-4">
          {/* Parte 1 — Desempenho: as métricas de avaliação, o que importa pra
               entender a rede/região (ordem: mais → menos importante). */}
          <div className="space-y-2.5">
            <ParteLabel>Desempenho {escopoLabel}</ParteLabel>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6">
              <KpiCard
                label={regiaoSel ? "Nota média" : "Nota média da rede"}
                value={brPt(m.nota)}
                suffix="/10"
                accent="var(--color-nota)"
                valueColor={gradeColor(m.nota)}
                icon={<Star size={16} />}
              />

              <FaixaKpiCompacta faixas={faixas.data} loading={faixas.loading} />
              <KpiCard
                label="Taxa de conclusão"
                value={m.conclusao == null ? "–" : Math.round(m.conclusao)}
                suffix={m.conclusao == null ? undefined : "%"}
                accent="var(--color-good)"
                icon={<CheckCircle2 size={16} />}
              />

              <KpiCard
                label="Score de engajamento"
                value={m.engajamento == null ? "–" : Math.round(m.engajamento)}
                suffix={m.engajamento != null ? "%" : undefined}
                accent="var(--color-chart-5)"
                sub={`média ${escopoLabel}`}
                icon={<Activity size={16} />}
              />

              <KpiCard
                label="Alunos inativos"
                value={m.inativos}
                accent="var(--color-bad)"
                sub="sem acessar a plataforma há mais de 7 dias"
                icon={<UserX size={16} />}
              />

              <KpiCard
                label="Tempo online"
                value={fmtTempo(m.tempo)}
                accent="var(--color-chart-6)"
                sub="soma do tempo online dos alunos"
                icon={<Clock size={16} />}
              />
            </div>
          </div>

          {/* Parte 2 — Estrutura/volume: os detalhes da rede/região. */}
          <div className="space-y-2.5">
            <ParteLabel>Estrutura {escopoLabel}</ParteLabel>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6">
              {!regiaoSel && (
                <KpiCard
                  label="Regiões"
                  value={k.regioes ?? "–"}
                  accent="var(--color-perfil-gestor)"
                  icon={<MapPin size={16} />}
                />
              )}
              <KpiCard
                label="Escolas"
                value={m.escolas}
                accent={regiaoSel ? "var(--color-perfil-gestor)" : undefined}
                icon={<School size={16} />}
              />

              <KpiCard
                label="Turmas"
                value={m.turmas}
                icon={<BookOpen size={16} />}
              />
              <KpiCard
                label="Alunos"
                value={m.alunos.toLocaleString("pt-BR")}
                icon={<GraduationCap size={16} />}
              />
              <KpiCard
                label="Professores"
                value={m.professores}
                icon={<Users2 size={16} />}
              />
              <KpiCard
                label="Execuções de provas"
                value={m.execucoes.toLocaleString("pt-BR")}
                accent="var(--color-good)"
                icon={<PlayCircle size={16} />}
              />
              {regiaoSel && (
                <KpiCard
                  label="Tarefas atribuídas"
                  value={m.tarefas.toLocaleString("pt-BR")}
                  accent="var(--color-accent)"
                  icon={<ClipboardList size={16} />}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="evolucao-temporal" className="scroll-mt-4">
        <SectionHead
          title={
            regiaoSel
              ? `Evolução temporal — ${regiaoSel.regional}`
              : "Evolução temporal da rede"
          }
          sub="média mensal"
        />

        <EvolucaoTemporal
          serie={evolucao.data ?? []}
          loading={evolucao.loading}
          vazioEngHint={
            regiaoSel
              ? "Nenhum score registrado para a região no período."
              : "Nenhum score registrado para a rede no período."
          }
        />
      </section>

      {!regiaoSel && (
        <>
          <section id="execucoes" className="scroll-mt-4">
            <SectionHead
              title="Execuções ao longo do tempo"
              sub="últimos 18 meses"
            />
            <Card>
              {execMensal.loading ? (
                <Skeleton className="h-[240px] w-full" />
              ) : execMensal.data?.length ? (
                <AreaChartCard
                  data={execMensal.data.map(r => ({ ...r }))}
                  xKey="mes"
                  series={[
                    {
                      key: "execucoes",
                      label: "Execuções",
                      color: "var(--color-chart-1)"
                    },
                    {
                      key: "alunos_ativos",
                      label: "Alunos ativos",
                      color: "var(--color-chart-3)"
                    }
                  ]}
                  height={260}
                />
              ) : (
                <EstadoVazio
                  icon={<LineChart size={20} />}
                  titulo="Sem execuções no período"
                  hint="Nenhum registro de execução nos últimos 18 meses."
                />
              )}
            </Card>
          </section>
        </>
      )}

      <section id="desempenho-disciplina" className="scroll-mt-4">
        <SectionHead
          title="Desempenho por disciplina"
          sub={`nota média · escala 0–10 · ${
            regiaoSel ? regiaoSel.regional : "rede inteira"
          }`}
        />

        <Card>
          {disciplinas.loading ? (
            <Skeleton className="h-[220px] w-full" />
          ) : disciplinas.data?.length ? (
            <NotaBarraLista
              data={disciplinas.data.map(d => ({
                nome: d.disciplina,
                nota: d.media ?? 0
              }))}
              labelKey="nome"
              valueKey="nota"
              labelWidth={120}
            />
          ) : (
            <EstadoVazio
              icon={<BarChart3 size={20} />}
              titulo="Sem avaliações suficientes"
              hint={
                regiaoSel
                  ? "Nenhuma disciplina com nota registrada nesta região ainda."
                  : "Nenhuma disciplina com nota registrada na rede ainda."
              }
            />
          )}
        </Card>
      </section>

      <section id="ranking-regioes" className="scroll-mt-4">
        <SectionHead
          title={
            regiaoSel
              ? `Escolas — ${regiaoSel.regional}`
              : semRede
              ? "Ranking de redes"
              : "Ranking de regiões"
          }
          sub={
            regiaoSel
              ? undefined
              : semRede
              ? "clique numa rede para ver as regiões dela"
              : "clique numa região para ver as escolas"
          }
        />

        <Card>
          {/* Três níveis no mesmo bloco, do mais amplo para o mais específico:
              redes (só a Educandus chega aqui) → regiões da rede → escolas da
              região. Cada um com a sua busca, e nenhuma delas alarga o que o
              perfil já podia ver: filtram o que a lista tem (redes e regiões) ou
              vão como `nome` na chamada já recortada (escolas). */}
          {semRede ? (
            redes.loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : redes.data?.length ? (
              <>
                <BuscaNome
                  id="ie-busca-rede"
                  value={buscaRede}
                  onChange={setBuscaRede}
                  placeholder="Buscar rede…"
                  total={redes.data.length}
                  filtrado={redesFiltradas.length}
                  rotulo="rede"
                />
                {redesFiltradas.length ? (
                  <RankingTabela
                    linhas={redesFiltradas}
                    colunas={colunasRedes}
                    rowKey={r => r.id}
                    sortDefault="execucoes"
                    onRowClick={selecionarRede}
                    rank
                  />
                ) : (
                  <SemResultadoBusca termo={buscaRede} item="rede" />
                )}
              </>
            ) : (
              <EstadoVazio
                icon={<Building2 size={20} />}
                titulo="Nenhuma rede encontrada"
                hint="Nenhuma rede ativa tem turmas com alunos no momento."
              />
            )
          ) : !regiaoSel ? (
            regioes.loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : regioes.data?.length ? (
              <>
                <BuscaNome
                  id="ie-busca-regiao"
                  value={buscaRegiao}
                  onChange={setBuscaRegiao}
                  placeholder="Buscar região…"
                  total={regioes.data.length}
                  filtrado={regioesFiltradas.length}
                  rotulo="região"
                  rotuloPlural="regiões"
                />
                {regioesFiltradas.length ? (
                  <RankingTabela
                    linhas={regioesFiltradas}
                    colunas={colunasRegioes}
                    rowKey={r => r.id}
                    sortDefault="execucoes"
                    onRowClick={selecionarRegiao}
                    rank
                  />
                ) : (
                  <SemResultadoBusca termo={buscaRegiao} item="região" />
                )}
              </>
            ) : (
              <EstadoVazio
                icon={<MapPin size={20} />}
                titulo="Nenhuma região encontrada"
                hint="Esta rede ainda não tem regiões com turmas ativas."
              />
            )
          ) : (
            <>
              {/* A busca de escolas fica FORA do bloco de loading: ela dispara a
                  própria recarga, e desmontar o input a cada tecla tiraria o foco
                  no meio da digitação. */}
              <BuscaNome
                id="ie-busca-escola"
                value={buscaEscola}
                onChange={setBuscaEscola}
                placeholder="Buscar escola…"
                rotulo="escola"
              />

              {escolas.loading ? (
                <Skeleton className="h-[300px] w-full" />
              ) : escolas.data?.rows.length ? (
                <>
                  <RankingTabela
                    linhas={escolas.data.rows}
                    colunas={colunasEscolas}
                    rowKey={r => r.id}
                    sortDefault="nota_media"
                    onRowClick={selecionarEscola}
                    rank
                  />

                  <div className="mt-3 flex items-center justify-between text-[11.5px] text-[var(--color-ink-faint)]">
                    <Chip tone="none">
                      {/* O total vem do servidor e já reflete a busca — dizer
                          "nesta região" com um filtro ativo seria impreciso. */}
                      {buscaEscolaServidor
                        ? `${escolas.data.total} escola${
                            escolas.data.total === 1 ? "" : "s"
                          } encontrada${escolas.data.total === 1 ? "" : "s"}`
                        : `${escolas.data.total} escolas nesta região`}
                    </Chip>
                  </div>
                </>
              ) : buscaEscolaServidor ? (
                <SemResultadoBusca termo={buscaEscolaServidor} item="escola" />
              ) : (
                <EstadoVazio
                  icon={<School size={20} />}
                  titulo="Nenhuma escola encontrada"
                  hint="Esta região ainda não tem escolas com turmas ativas."
                />
              )}
            </>
          )}
        </Card>
      </section>
    </div>
  );
}

/** Rótulo discreto de cada bloco do cabeçalho ("Desempenho da rede" etc.). */
function ParteLabel({ children }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-ink-faint)]">
      {children}
    </p>
  );
}

function PainelSkeleton() {
  return (
    <div className="space-y-7">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-[104px]" />
        ))}
      </div>
      <Skeleton className="h-[260px]" />
      <Skeleton className="h-[320px]" />
    </div>
  );
}
